import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { customersApi } from '../../lib/api/customers';
import { productsApi, Product } from '../../lib/api/products';
import { billingApi, BillingOrder, TimelineEvent as ApiTimelineEvent } from '../../lib/api/billing';
import { rateLimitApi, BlockedEntity } from '../../lib/api/rateLimit';
import { aiUsageApi, AIUsageSummary } from '../../lib/api/ai-usage';
import usageApi, { UserUsageResponse } from '../../lib/api/usage';
import { Customer } from './types';
import { ArrowLeft, CheckCircle2, XCircle, AlertCircle, CreditCard, UserPlus, Info, Shield, ShieldAlert, Unlock, Banknote, Ban, Trash2 } from 'lucide-react';
import { ErrorStatusPage } from '../../components/ErrorStatusPage';
import { ConfirmationModal } from '../../components/ConfirmationModal';
import { DangerousConfirmationModal } from '../../components/DangerousConfirmationModal';

type DangerAction = 'ban' | 'unban' | 'remove';

const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() }).format(amount / 100);
};

export const CustomerDetailsPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [customer, setCustomer] = useState<Customer | null>(null);
    const [plans, setPlans] = useState<Product[]>([]);
    const [orders, setOrders] = useState<BillingOrder[]>([]);
    const [dbEvents, setDbEvents] = useState<ApiTimelineEvent[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [aiUsage, setAiUsage] = useState<AIUsageSummary | null>(null);
    const [serviceUsage, setServiceUsage] = useState<UserUsageResponse | null>(null);
    const [loadingAi, setLoadingAi] = useState(false);

    // Rate Limit State
    const [blockInfo, setBlockInfo] = useState<BlockedEntity | null>(null);
    const [unblocking, setUnblocking] = useState(false);

    // Form state
    const [formData, setFormData] = useState({
        name: '',
        email: ''
    });

    const [stripeCustomer, setStripeCustomer] = useState<any>(null);
    const [customerBalance, setCustomerBalance] = useState<{ balance: number; currency: string } | null | undefined>(undefined);
    const [isEditingAddress, setIsEditingAddress] = useState(false);
    const [addressForm, setAddressForm] = useState({ line1: '', city: '', state: '', postalCode: '', country: '' });

    // Subscription Action State
    const [isChangingPlan, setIsChangingPlan] = useState(false);
    const [selectedNewPlanId, setSelectedNewPlanId] = useState('');
    const [selectedNewPriceId, setSelectedNewPriceId] = useState('');
    const [forceImmediate, setForceImmediate] = useState(false);
    const [processingSubAction, setProcessingSubAction] = useState(false);

    // Modal State
    const [modalConfig, setModalConfig] = useState<{
        isOpen: boolean;
        title: string;
        message: string;
        variant?: 'warning' | 'danger' | 'info';
        confirmText?: string;
        onConfirm: () => void;
    }>({
        isOpen: false,
        title: '',
        message: '',
        onConfirm: () => { }
    });

    const closeModal = () => setModalConfig(prev => ({ ...prev, isOpen: false }));

    // Dangerous action modal state
    const [dangerAction, setDangerAction] = useState<DangerAction | null>(null);
    const [processingDanger, setProcessingDanger] = useState(false);

    useEffect(() => {
        if (id) {
            loadData(id);
        }
    }, [id]);

    const loadData = async (customerId: string) => {
        try {
            setLoading(true);
            const [customerData, plansData, ordersData, stripeData, balanceData, blockedList] = await Promise.all([
                customersApi.getById(customerId),
                productsApi.list().catch(() => []),
                billingApi.getCustomerOrders(customerId).catch(() => []),
                billingApi.getCustomerDetails(customerId).catch(() => null),
                billingApi.getCustomerBalance(customerId).catch(() => null),
                rateLimitApi.getBlockedUsers().catch(() => [])
            ]);


            setCustomer(customerData);
            setPlans(plansData);
            setOrders(ordersData);
            setStripeCustomer(stripeData);

            // Set balance: prefer dedicated API, fallback to stripeCustomer data
            if (balanceData) {
                setCustomerBalance(balanceData);
            } else if (stripeData && stripeData.balance !== undefined) {
                setCustomerBalance({
                    balance: stripeData.balance,
                    currency: stripeData.currency || 'eur'
                });
            } else {
                setCustomerBalance(null);
            }

            // Check if user is blocked
            const userKey = `user:${customerId}`;
            const blocked = blockedList.find((b: BlockedEntity) => b.key === userKey);
            setBlockInfo(blocked || null);

            if (stripeData?.address) {
                setAddressForm({
                    line1: stripeData.address.line1 || '',
                    city: stripeData.address.city || '',
                    state: stripeData.address.state || '',
                    postalCode: stripeData.address.postal_code || '',
                    country: stripeData.address.country || ''
                });
            }

            // Fetch DB timeline
            try {
                const events = await billingApi.getUserTimeline(customerId);
                setDbEvents(events);
            } catch (e) {
                console.warn('Failed to load timeline events', e);
            }

            setFormData({
                name: customerData.name || '',
                email: customerData.email || ''
            });

            // Fetch AI usage separately (don't block main load)
            fetchAiUsage(customerId);
        } catch (err: any) {
            if (err.response?.status === 404) {
                setError('not_found');
            } else if (err.response?.status >= 500 || !err.response) {
                setError('service_unavailable');
            } else {
                setError('generic');
            }
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const fetchAiUsage = async (customerId: string) => {
        try {
            setLoadingAi(true);
            const [aiData, usageData] = await Promise.all([
                aiUsageApi.getUserUsage(customerId),
                usageApi.getUserUsage(customerId)
            ]);
            setAiUsage(aiData);
            setServiceUsage(usageData);
        } catch (e) {
            console.warn('Failed to fetch usage data', e);
        } finally {
            setLoadingAi(false);
        }
    };

    const handleSave = async () => {
        if (!id || !customer) return;
        try {
            setSaving(true);
            const updateData = {
                name: formData.name,
                email: formData.email
            };

            await customersApi.update(id, updateData);
            await loadData(id);
        } catch (err) {
            setError('Unable to save changes. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const handleSaveAddress = async () => {
        if (!id) return;
        try {
            const res = await billingApi.updateCustomerAddress(id, addressForm);
            setStripeCustomer(res);
            setIsEditingAddress(false);
        } catch (error) {
            console.error(error);
            setError('Failed to update billing address');
        }
    };

    const handleUnblock = async () => {
        if (!id) return;
        try {
            setUnblocking(true);
            await rateLimitApi.unblockUser(`user:${id}`);
            setBlockInfo(null);
        } catch (error) {
            console.error('Failed to unblock user:', error);
            setModalConfig({
                isOpen: true,
                title: 'Unblock Failed',
                message: 'Failed to unblock the user. Please try again or check logs.',
                variant: 'danger',
                confirmText: 'OK',
                onConfirm: closeModal
            });
        } finally {
            setUnblocking(false);
        }
    };

    // Dangerous actions (ban / unban / remove)
    const dangerCopy: Record<DangerAction, { title: string; label: string; message: string }> = {
        ban: {
            title: 'Ban User',
            label: 'Ban User',
            message: 'This account will be disabled. The user will be signed out and prevented from logging back in until unbanned. Existing data is preserved.'
        },
        unban: {
            title: 'Unban User',
            label: 'Unban User',
            message: 'This will re-enable the account. The user will be able to log in again on their next attempt.'
        },
        remove: {
            title: 'Remove User',
            label: 'Remove User',
            message: 'This will permanently delete the user and all associated data (boards, channels, journals, subscriptions history). This action cannot be undone.'
        }
    };

    const handleDangerConfirm = async () => {
        if (!id || !dangerAction) return;
        try {
            setProcessingDanger(true);
            if (dangerAction === 'ban') {
                await customersApi.ban(id);
                await loadData(id);
            } else if (dangerAction === 'unban') {
                await customersApi.unban(id);
                await loadData(id);
            } else if (dangerAction === 'remove') {
                await customersApi.remove(id);
                setDangerAction(null);
                navigate('/customers');
                return;
            }
            setDangerAction(null);
        } catch (err: any) {
            console.error('Danger action failed:', err);
            setDangerAction(null);
            const action = dangerCopy[dangerAction];
            setModalConfig({
                isOpen: true,
                title: `${action.label} Failed`,
                message: err?.response?.data?.error || `Failed to ${dangerAction} the user. Please try again.`,
                variant: 'danger',
                confirmText: 'OK',
                onConfirm: closeModal
            });
        } finally {
            setProcessingDanger(false);
        }
    };

    // Subscription Actions
    const executeChangePlan = async () => {
        if (!id || !selectedNewPriceId) return;
        closeModal();
        try {
            setProcessingSubAction(true);

            await billingApi.changeCustomerPlan(id, selectedNewPriceId, forceImmediate);
            await loadData(id);
            setIsChangingPlan(false);
            setSelectedNewPlanId('');
            setSelectedNewPriceId('');
            setForceImmediate(false);
        } catch (error) {
            console.error(error);
            setModalConfig({
                isOpen: true,
                title: 'Change Failed',
                message: 'Failed to change the subscription plan. Please try again.',
                variant: 'danger',
                onConfirm: closeModal
            });
        } finally {
            setProcessingSubAction(false);
        }
    };

    const handleChangePlanRequest = () => {
        if (!id || !selectedNewPriceId) return;

        let message = 'Are you sure you want to change the subscription plan?';
        if (forceImmediate) {
            message += ' This will be applied IMMEDIATELY regardless of upgrade/downgrade status.';
        } else {
            message += ' Upgrades will optionally be prorated immediately, while downgrades will typically be scheduled for the end of the period.';
        }

        setModalConfig({
            isOpen: true,
            title: 'Change Subscription Plan',
            message,
            variant: 'warning',
            confirmText: 'Change Plan',
            onConfirm: executeChangePlan
        });
    };

    const executeCancelSubscription = async (immediately: boolean) => {
        if (!id) return;
        closeModal();
        try {
            setProcessingSubAction(true);
            await billingApi.cancelCustomerSubscription(id, immediately);
            await loadData(id);
        } catch (error) {
            console.error(error);
            setModalConfig({
                isOpen: true,
                title: 'Cancellation Failed',
                message: 'Failed to cancel the subscription. Please try again.',
                variant: 'danger',
                onConfirm: closeModal
            });
        } finally {
            setProcessingSubAction(false);
        }
    };

    const handleCancelRequest = (immediately: boolean) => {
        if (!id) return;
        const msg = immediately
            ? 'Are you sure you want to cancel IMMEDIATELY? The user will lose access now. This action cannot be easily undone.'
            : 'Are you sure you want to cancel at the end of the billing period? The user will retain access until then.';

        setModalConfig({
            isOpen: true,
            title: immediately ? 'Cancel immediately?' : 'Cancel at period end?',
            message: msg,
            variant: immediately ? 'danger' : 'warning',
            confirmText: immediately ? 'Cancel Subscription' : 'Schedule Cancellation',
            onConfirm: () => executeCancelSubscription(immediately)
        });
    };

    const executeResumeSubscription = async () => {
        if (!id) return;
        closeModal();
        try {
            setProcessingSubAction(true);
            await billingApi.resumeCustomerSubscription(id);
            await loadData(id);
        } catch (error) {
            console.error(error);
            setModalConfig({
                isOpen: true,
                title: 'Resume Failed',
                message: 'Failed to resume the subscription.',
                variant: 'danger',
                onConfirm: closeModal
            });
        } finally {
            setProcessingSubAction(false);
        }
    };

    const handleResumeRequest = () => {
        setModalConfig({
            isOpen: true,
            title: 'Resume Subscription',
            message: 'Are you sure you want to resume this canceled subscription?',
            variant: 'info',
            confirmText: 'Resume Subscription',
            onConfirm: executeResumeSubscription
        });
    };

    if (loading) return <div className="p-8 text-center text-neutral-500">Loading...</div>;
    if (error && !customer) {
        return (
            <ErrorStatusPage
                type={(error as 'service_unavailable' | 'not_found' | 'generic') || 'not_found'}
                onRetry={id ? () => loadData(id) : undefined}
            />
        );
    }
    if (!customer) return (
        <ErrorStatusPage type="not_found" />
    );

    const activeSubscription = customer.subscription;

    const getStatusColor = (status?: string) => {
        switch (status) {
            case 'active': return 'bg-green-50 text-green-700 border-green-100';
            case 'past_due': return 'bg-red-50 text-red-700 border-red-100';
            case 'canceled': return 'bg-neutral-100 text-neutral-600 border-neutral-200';
            case 'trialing': return 'bg-blue-50 text-blue-700 border-blue-100';
            default: return 'bg-neutral-100 text-neutral-600 border-neutral-200';
        }
    };

    // Timeline logic (simplified for brevity, reused from original)
    // ... Timeline Construction
    interface TimelineEvent {
        id: string;
        date: Date;
        title: string;
        description?: React.ReactNode;
        time: string;
        icon: React.ReactNode;
    }

    const getTimelineEvents = (): TimelineEvent[] => {
        if (!customer) return [];
        const events: TimelineEvent[] = [];

        // 1. User Creation
        events.push({
            id: 'created',
            date: new Date(customer.createdAt),
            title: 'Customer created',
            description: `Profile created for ${customer.name || customer.email}`,
            time: new Date(customer.createdAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true }),
            icon: <UserPlus size={14} className="text-neutral-500" />
        });

        // 3. Billing Orders (Payments)
        (orders || []).forEach(order => {
            // ... logic from previous file ...
            // Simplified for this overwrite:
            const hasDbEvents = (dbEvents || []).some(e => e.billingOrderId === order.id);
            if (hasDbEvents) return;

            let icon = <CreditCard size={14} className="text-neutral-500" />;
            if (order.status === 'PAID') icon = <CheckCircle2 size={14} className="text-green-600" />;
            else if (order.status === 'FAILED') icon = <XCircle size={14} className="text-red-600" />;

            let description = <span className="text-neutral-600">{order.status} - {formatCurrency(order.amount, order.currency)}</span>;

            if (order.metadata) {
                try {
                    const meta = JSON.parse(order.metadata);
                    const brand = meta.card_brand || meta.brand;
                    const last4 = meta.card_last4 || meta.last4;
                    const walletType = meta.wallet_type;

                    if (brand && last4) {
                        const currency = order.currency.toUpperCase();
                        const amount = (order.amount / 100).toFixed(2);

                        let paymentMethodStr = `${brand.charAt(0).toUpperCase() + brand.slice(1)} ending in ${last4}`;
                        if (walletType) {
                            const walletName = walletType.split('_').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
                            paymentMethodStr += ` via ${walletName}`;
                        }
                        description = <span className="text-neutral-900">A {currency} {amount} payment was processed using a {paymentMethodStr}.</span>;
                    }
                } catch (e) {
                    // Ignore parse error
                }
            }

            events.push({
                id: `order_${order.id}`,
                date: new Date(order.date),
                title: `Order #${order.orderNumber}`,
                description: description,
                time: new Date(order.date).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true }),
                icon: icon
            });
        });

        // 4. DB Events
        dbEvents.forEach(evt => {
            let icon = <Info size={14} className="text-neutral-500" />;
            if (evt.category === 'payment') {
                if (evt.type === 'payment_success') icon = <CheckCircle2 size={14} className="text-green-600" />;
                else if (evt.type === 'payment_failed') icon = <XCircle size={14} className="text-red-600" />;
            } else if (evt.category === 'subscription') {
                if (evt.type === 'subscription_created') icon = <CheckCircle2 size={14} className="text-blue-500" />;
                else if (evt.type === 'subscription_canceled') icon = <XCircle size={14} className="text-neutral-500" />;
            }
            events.push({
                id: evt.id,
                date: new Date(evt.createdAt),
                title: evt.title,
                description: evt.description,
                time: new Date(evt.createdAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true }),
                icon: icon
            });
        });

        return events.sort((a, b) => b.date.getTime() - a.date.getTime());
    };

    const timelineEvents = getTimelineEvents();
    const groupedGroups: { date: string, events: TimelineEvent[] }[] = [];
    let currentDate = '';
    let currentGroup: TimelineEvent[] = [];

    timelineEvents.forEach(event => {
        const dateStr = event.date.toLocaleDateString([], { month: 'long', day: 'numeric' });
        if (dateStr !== currentDate) {
            if (currentGroup.length > 0) groupedGroups.push({ date: currentDate, events: currentGroup });
            currentDate = dateStr;
            currentGroup = [];
        }
        currentGroup.push(event);
    });
    if (currentGroup.length > 0) groupedGroups.push({ date: currentDate, events: currentGroup });

    return (
        <div className="h-full flex flex-col p-1 animate-fade-in max-w-6xl mx-auto w-full">
            {/* Page Header */}
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-4">
                    <button onClick={() => navigate('/customers')} className="btn-ghost !p-1.5">
                        <ArrowLeft size={16} />
                    </button>
                    <div>
                        <h1 className="text-xl font-bold text-neutral-900">{customer.name || 'Unnamed Customer'}</h1>
                        <div className="flex items-center gap-2 text-xs text-neutral-500">
                            <span>{customer.email}</span>
                            <span>•</span>
                            <span>ID: {customer.id}</span>
                        </div>
                    </div>
                </div>
            </div>

            {error && (
                <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-xs border border-red-100">{error}</div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                {/* Main Content (Left Column) */}
                <div className="lg:col-span-8 space-y-4">

                    {/* Current Subscription Section */}
                    <div className="bg-white rounded-lg border border-neutral-200 shadow-sm overflow-hidden">
                        <div className="px-4 py-3 border-b border-neutral-100 flex justify-between items-center bg-neutral-50/50">
                            <h2 className="text-sm font-semibold text-neutral-900">Current Subscription</h2>
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={() => navigate(`/customers/${customer?.id}/invoices`)}
                                    className="text-xs text-blue-600 hover:text-blue-700 hover:underline flex items-center gap-1"
                                >
                                    View Invoices
                                </button>
                                {activeSubscription && (
                                    <span className={`px-2 py-0.5 rounded text-xs font-medium border ${getStatusColor(activeSubscription.status)}`}>
                                        {activeSubscription.status?.toUpperCase()}
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="p-6">
                            {/* Plan Display */}
                            <div className="flex items-start justify-between mb-6">
                                <div>
                                    <h3 className="text-lg font-medium text-neutral-900">
                                        {activeSubscription?.plan?.name || 'No Active Plan'}
                                    </h3>
                                    {activeSubscription?.plan?.prices?.[0] && (
                                        <p className="text-neutral-500 text-sm">
                                            {formatCurrency(activeSubscription.plan.prices[0].amount, activeSubscription.plan.prices[0].currency)}
                                            /{activeSubscription.plan.prices[0].interval}
                                            {activeSubscription.plan.prices[0].billing_interval && activeSubscription.plan.prices[0].billing_interval !== activeSubscription.plan.prices[0].interval && (
                                                <span className="text-xs text-neutral-400 ml-1">
                                                    (billed {activeSubscription.plan.prices[0].billing_interval === 'year' ? 'annually' : 'monthly'})
                                                </span>
                                            )}
                                        </p>
                                    )}
                                    {activeSubscription?.currentPeriodEnd && (
                                        <p className="text-xs text-neutral-400 mt-1">
                                            {activeSubscription.status === 'canceled' ? 'Expires' : 'Renews'} on {new Date(activeSubscription.currentPeriodEnd).toLocaleDateString()}
                                        </p>
                                    )}
                                </div>
                                {!isChangingPlan && activeSubscription && activeSubscription.status !== 'canceled' && (
                                    <button
                                        onClick={() => setIsChangingPlan(true)}
                                        className="btn-outline text-xs"
                                        disabled={processingSubAction}
                                    >
                                        Change Plan
                                    </button>
                                )}
                            </div>

                            {/* Change Plan Mode */}
                            {isChangingPlan && (
                                <div className="bg-neutral-50 p-4 rounded-lg border border-neutral-100 mb-6 space-y-4">
                                    <div>
                                        <label className="block text-xs font-medium text-neutral-700 mb-2">Select Product</label>
                                        <select
                                            value={selectedNewPlanId}
                                            onChange={(e) => {
                                                setSelectedNewPlanId(e.target.value);
                                                setSelectedNewPriceId(''); // Reset price when product changes
                                            }}
                                            className="w-full text-sm border border-neutral-300 rounded px-2 py-1"
                                        >
                                            <option value="">Select a product...</option>
                                            {plans.map(p => (
                                                <option key={p.id} value={p.id}>{p.name}</option>
                                            ))}
                                        </select>
                                    </div>

                                    {selectedNewPlanId && (
                                        <div>
                                            <label className="block text-xs font-medium text-neutral-700 mb-2">Select Price</label>
                                            <select
                                                value={selectedNewPriceId}
                                                onChange={(e) => setSelectedNewPriceId(e.target.value)}
                                                className="w-full text-sm border border-neutral-300 rounded px-2 py-1"
                                            >
                                                <option value="">Select a price...</option>
                                                {plans.find(p => p.id === selectedNewPlanId)?.variants?.[0]?.prices?.map(price => (
                                                    <option key={price.id} value={price.id}>
                                                        {formatCurrency(price.amount, price.currency)} / {price.interval}
                                                        {price.billing_interval && price.billing_interval !== price.interval &&
                                                            ` (billed ${price.billing_interval === 'year' ? 'annually' : 'monthly'})`
                                                        }
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    )}

                                    <div className="flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            id="immediateChange"
                                            checked={forceImmediate}
                                            onChange={(e) => setForceImmediate(e.target.checked)}
                                            className="rounded border-neutral-300"
                                        />
                                        <label htmlFor="immediateChange" className="text-xs text-neutral-600">
                                            Force immediate change (override scheduled behavior)
                                        </label>
                                    </div>

                                    <div className="flex gap-2">
                                        <button
                                            onClick={handleChangePlanRequest}
                                            disabled={!selectedNewPriceId || processingSubAction}
                                            className="btn-primary text-xs"
                                        >
                                            Confirm Change
                                        </button>
                                        <button onClick={() => setIsChangingPlan(false)} className="btn-ghost text-xs">
                                            Cancel
                                        </button>
                                    </div>

                                    <p className="text-[10px] text-neutral-500">
                                        Note: For downgrades, the change is scheduled for period end unless "Force immediate" is checked.
                                    </p>
                                </div>
                            )}

                            {/* Actions */}
                            <div className="border-t border-neutral-100 pt-4 flex flex-wrap gap-2 text-xs">
                                {activeSubscription?.status === 'active' && (
                                    <>
                                        <button
                                            onClick={() => handleCancelRequest(false)}
                                            className="px-3 py-1.5 rounded border border-neutral-200 hover:border-red-200 hover:bg-red-50 text-neutral-600 hover:text-red-600 transition-colors"
                                            disabled={processingSubAction}
                                        >
                                            Cancel at End of Period
                                        </button>
                                        <button
                                            onClick={() => handleCancelRequest(true)}
                                            className="px-3 py-1.5 rounded border border-neutral-200 hover:border-red-200 hover:bg-red-50 text-neutral-600 hover:text-red-600 transition-colors"
                                            disabled={processingSubAction}
                                        >
                                            Cancel Immediately
                                        </button>
                                    </>
                                )}
                                {/* Resume logic (if cancel scheduled - simpler implementation check) */}
                                {/* In our simplified type we might not know if it is scheduled to cancel, but if it is 'active' we show cancel options. */}

                                {activeSubscription?.status === 'canceled' && (
                                    <div className="flex items-center gap-2 text-neutral-500">
                                        <AlertCircle size={14} />
                                        <span>Subscription is canceled.</span>
                                        <button
                                            onClick={handleResumeRequest}
                                            className="text-blue-600 hover:underline ml-1"
                                            disabled={processingSubAction}
                                        >
                                            Reactivate?
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Timeline (Reused layout) */}
                    <div className="bg-white rounded-lg border border-neutral-200 shadow-sm overflow-hidden">
                        <div className="px-4 py-3 border-b border-neutral-100 bg-neutral-50/50">
                            <h2 className="text-sm font-semibold text-neutral-900">Timeline</h2>
                        </div>
                        <div className="p-6">
                            {groupedGroups.map((group, idx) => (
                                <div key={idx} className="relative mb-6 last:mb-0">
                                    <div className="sticky top-0 bg-white z-10 mb-4">
                                        <h3 className="text-xs font-semibold text-neutral-900 uppercase tracking-wide bg-neutral-100 inline-block px-2 py-1 rounded">{group.date}</h3>
                                    </div>
                                    <div className="ml-2 pl-6 border-l-2 border-neutral-100 space-y-8 pb-2">
                                        {group.events.map(event => (
                                            <div key={event.id} className="relative">
                                                <div className="absolute -left-[31px] bg-white p-1 rounded-full border border-neutral-200">{event.icon}</div>
                                                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-1 sm:gap-4">
                                                    <div className="flex-1 text-sm text-neutral-900">{event.description || event.title}</div>
                                                    <div className="text-xs text-neutral-400 whitespace-nowrap font-medium">{event.time}</div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Sidebar (Right Column) */}
                <div className="lg:col-span-4 space-y-4">

                    {/* Customer Overview Card */}
                    <div className="bg-white rounded-lg border border-neutral-200 shadow-sm overflow-hidden">
                        <div className="px-4 py-3 border-b border-neutral-100 bg-neutral-50/50 flex justify-between items-center">
                            <h2 className="text-sm font-semibold text-neutral-900">Customer Details</h2>
                            <button onClick={handleSave} className="text-xs text-blue-600 hover:underline">{saving ? 'Saving...' : 'Save'}</button>
                        </div>
                        <div className="p-4 space-y-4 text-sm">
                            <div>
                                <label className="block text-xs font-medium text-neutral-500 mb-1">Name</label>
                                <input
                                    value={formData.name}
                                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                                    className="w-full border border-neutral-200 rounded px-2 py-1.5 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-neutral-500 mb-1">Email</label>
                                <input
                                    value={formData.email}
                                    onChange={e => setFormData({ ...formData, email: e.target.value })}
                                    className="w-full border border-neutral-200 rounded px-2 py-1.5 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all"
                                />
                            </div>
                            <div className="pt-2 border-t border-neutral-100 mt-2">
                                <span className="text-xs text-neutral-400">Created {customer ? new Date(customer.createdAt).toLocaleDateString() : ''}</span>
                            </div>
                        </div>
                    </div>

                    {/* Account Balance Card */}
                    <div className="bg-white rounded-lg border border-neutral-200 shadow-sm overflow-hidden">
                        <div className="px-4 py-3 border-b border-neutral-100 bg-neutral-50/50 flex justify-between items-center">
                            <h2 className="text-sm font-semibold text-neutral-900">Account Balance</h2>
                            <Banknote size={16} className="text-neutral-400" />
                        </div>
                        <div className="p-4">
                            {customerBalance === null ? (
                                <div className="text-xs text-neutral-400 italic text-center py-2">No billing account</div>
                            ) : customerBalance === undefined ? (
                                <div className="text-xs text-neutral-400 italic text-center py-2">Loading balance...</div>
                            ) : (
                                <div className="flex flex-col items-center justify-center py-2 text-center">
                                    <div className={`text-2xl font-bold ${customerBalance.balance < 0 ? 'text-green-600' : customerBalance.balance > 0 ? 'text-red-600' : 'text-neutral-900'}`}>
                                        {formatCurrency(Math.abs(customerBalance.balance || 0), customerBalance.currency)}
                                    </div>
                                    <p className="text-[10px] uppercase tracking-wider font-semibold text-neutral-400 mt-1">
                                        {customerBalance.balance < 0 ? 'Available Credit' : customerBalance.balance > 0 ? 'Outstanding Balance' : 'Clear Balance'}
                                    </p>
                                    {customerBalance.balance < 0 && (
                                        <p className="text-[10px] text-neutral-500 mt-2 bg-green-50 px-2 py-1 rounded-full border border-green-100">
                                            Will be applied to next invoice
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Rate Limit & Security Card */}
                    <div className="bg-white rounded-lg border border-neutral-200 shadow-sm overflow-hidden">
                        {/* ... Reusing existing logic ... */}
                        <div className="px-4 py-3 border-b border-neutral-100 bg-neutral-50/50 flex justify-between items-center">
                            <h2 className="text-sm font-semibold text-neutral-900">Security & Rate Limits</h2>
                            <div className="flex items-center gap-1">
                                {blockInfo ? (
                                    <span className="flex items-center gap-1 text-xs font-medium text-red-600 bg-red-50 px-2 py-0.5 rounded-full border border-red-100">
                                        <ShieldAlert size={10} /> Blocked
                                    </span>
                                ) : (
                                    <span className="flex items-center gap-1 text-xs font-medium text-green-600 bg-green-50 px-2 py-0.5 rounded-full border border-green-100">
                                        <Shield size={10} /> Normal
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="p-4 text-sm">
                            {blockInfo ? (
                                <div className="space-y-3">
                                    <p className="text-xs text-neutral-600">This user is currently blocked by the smart rate limiter.</p>
                                    <div className="bg-red-50 p-2 rounded text-xs border border-red-100 space-y-1">
                                        <div className="font-semibold text-red-800">Reason:</div>
                                        <div className="text-red-700">{blockInfo.reason}</div>
                                        <div className="text-red-700 pt-1 border-t border-red-100 mt-1">Expires: {new Date(blockInfo.until).toLocaleString()}</div>
                                    </div>
                                    <button onClick={handleUnblock} disabled={unblocking} className="w-full btn-outline-danger flex items-center justify-center gap-2 py-1.5 text-xs">
                                        <Unlock size={12} /> {unblocking ? 'Unblocking...' : 'Unblock User'}
                                    </button>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    <p className="text-xs text-neutral-500">No active restrictions. The user has full access to the platform.</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Variable Service Costs Card */}
                    <div className="bg-white rounded-lg border border-neutral-200 shadow-sm overflow-hidden">
                        <div className="px-4 py-3 border-b border-neutral-100 bg-neutral-50/50 flex justify-between items-center">
                            <h2 className="text-sm font-semibold text-neutral-900 flex items-center gap-2">
                                <Banknote size={16} className="text-emerald-500" />
                                Variable Service Cost
                            </h2>
                            {serviceUsage && (
                                <span className="text-[10px] font-bold text-neutral-500 uppercase">
                                    Lifetime Total
                                </span>
                            )}
                        </div>
                        <div className="p-4 space-y-4">
                            {!serviceUsage && loadingAi ? (
                                <div className="text-xs text-neutral-400 italic text-center py-4">Loading usage data...</div>
                            ) : !serviceUsage ? (
                                <div className="text-xs text-neutral-400 italic text-center py-4">No usage data available.</div>
                            ) : (
                                <>
                                    {/* Total Cost Display */}
                                    <div className="text-center py-2">
                                        <div className="text-[10px] font-semibold text-neutral-500 uppercase tracking-wider mb-0.5">Estimated Total Cost</div>
                                        <div className="text-3xl font-bold text-neutral-900">${serviceUsage.totalCost.toFixed(3)}</div>
                                    </div>

                                    {/* Service Breakdown */}
                                    <div className="space-y-3 pt-2">
                                        <div className="text-[10px] font-semibold text-neutral-500 uppercase tracking-wider">Breakdown by Service</div>
                                        
                                        {Object.entries(serviceUsage.serviceBreakdown).map(([service, data]) => (
                                            <div key={service} className="space-y-1">
                                                <div className="flex justify-between text-xs">
                                                    <span className="text-neutral-600 capitalize">{service.replace('-', ' ')}</span>
                                                    <span className="font-semibold text-neutral-900">${data.total.toFixed(4)}</span>
                                                </div>
                                                <div className="w-full h-1 bg-neutral-100 rounded-full overflow-hidden">
                                                    <div 
                                                        className="h-full bg-emerald-500" 
                                                        style={{ width: `${(data.total / serviceUsage.totalCost) * 100}%` }}
                                                    />
                                                </div>
                                                <div className="text-[10px] text-neutral-400">
                                                    {data.usageValue.toLocaleString()} {data.usageType}
                                                </div>
                                            </div>
                                        ))}

                                        {Object.keys(serviceUsage.serviceBreakdown).length === 0 && (
                                            <div className="text-xs text-neutral-400 italic">No billable service events recorded.</div>
                                        )}
                                    </div>

                                    {/* AI Quota Progress (Sub-section) */}
                                    {aiUsage && (
                                        <div className="pt-4 border-t border-neutral-100 space-y-2">
                                            <div className="flex justify-between text-[10px] font-semibold text-neutral-500 uppercase tracking-wider">
                                                <span>AI Quota Status</span>
                                                <span className={aiUsage.currentWindow.percentUsed >= 90 ? 'text-red-500' : 'text-purple-500'}>
                                                    {aiUsage.currentWindow.percentUsed}% Used
                                                </span>
                                            </div>
                                            <div className="w-full h-1 bg-neutral-100 rounded-full overflow-hidden">
                                                <div
                                                    className={`h-full transition-all duration-500 ${aiUsage.currentWindow.percentUsed >= 90 ? 'bg-red-500' : 'bg-purple-500'}`}
                                                    style={{ width: `${aiUsage.currentWindow.percentUsed}%` }}
                                                />
                                            </div>
                                            <div className="flex justify-between text-[10px] text-neutral-400">
                                                <span>{aiUsage.currentWindow.remaining === -1 ? 'Unlimited' : `${aiUsage.currentWindow.remaining.toLocaleString()} tokens left`}</span>
                                                <span>{aiUsage.quotaConfig.planName}</span>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>

                    {/* Billing Address Card */}
                    <div className="bg-white rounded-lg border border-neutral-200 shadow-sm overflow-hidden">
                        <div className="px-4 py-3 border-b border-neutral-100 bg-neutral-50/50 flex justify-between items-center">
                            <h2 className="text-sm font-semibold text-neutral-900">Billing Address</h2>
                            {!isEditingAddress ? (
                                <button onClick={() => setIsEditingAddress(true)} className="text-xs text-blue-600 hover:underline flex items-center gap-1">Edit</button>
                            ) : (
                                <div className="flex gap-2">
                                    <button onClick={() => setIsEditingAddress(false)} className="text-xs text-neutral-500 hover:text-neutral-700">Cancel</button>
                                    <button onClick={handleSaveAddress} className="text-xs text-blue-600 hover:underline">Save</button>
                                </div>
                            )}
                        </div>
                        <div className="p-4 text-sm">
                            {isEditingAddress ? (
                                <div className="space-y-3">
                                    <input placeholder="Address Line 1" className="w-full border border-neutral-200 rounded px-2 py-1.5 outline-none focus:border-blue-500" value={addressForm.line1} onChange={e => setAddressForm({ ...addressForm, line1: e.target.value })} />
                                    <input placeholder="City" className="w-full border border-neutral-200 rounded px-2 py-1.5 outline-none focus:border-blue-500" value={addressForm.city} onChange={e => setAddressForm({ ...addressForm, city: e.target.value })} />
                                    <input placeholder="State" className="w-full border border-neutral-200 rounded px-2 py-1.5 outline-none focus:border-blue-500" value={addressForm.state} onChange={e => setAddressForm({ ...addressForm, state: e.target.value })} />
                                    <input placeholder="ZIP / Postal Code" className="w-full border border-neutral-200 rounded px-2 py-1.5 outline-none focus:border-blue-500" value={addressForm.postalCode} onChange={e => setAddressForm({ ...addressForm, postalCode: e.target.value })} />
                                    <input placeholder="Country" className="w-full border border-neutral-200 rounded px-2 py-1.5 outline-none focus:border-blue-500" value={addressForm.country} onChange={e => setAddressForm({ ...addressForm, country: e.target.value })} />
                                </div>
                            ) : (
                                <div className="text-neutral-600">
                                    {stripeCustomer && stripeCustomer.address && (stripeCustomer.address.line1 || stripeCustomer.address.city) ? (
                                        <>
                                            <p>{stripeCustomer.address.line1}</p>
                                            {stripeCustomer.address.line2 && <p>{stripeCustomer.address.line2}</p>}
                                            <p>{[stripeCustomer.address.city, stripeCustomer.address.state, stripeCustomer.address.postal_code].filter(Boolean).join(', ')}</p>
                                            <p>{stripeCustomer.address.country}</p>
                                        </>
                                    ) : (
                                        <p className="text-neutral-400 italic">No address on file.</p>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Danger Zone Card */}
                    <div className="bg-white rounded-lg border border-error-500/30 shadow-sm overflow-hidden">
                        <div className="px-4 py-3 border-b border-error-500/20 bg-error-50/50 flex items-center gap-2">
                            <AlertCircle size={14} className="text-error-600" />
                            <h2 className="text-sm font-semibold text-error-600">Danger Zone</h2>
                        </div>
                        <div className="p-4 space-y-4 text-sm">
                            <div className="flex items-start justify-between gap-3">
                                <div className="flex-1">
                                    <p className="text-xs font-semibold text-neutral-800">
                                        {customer.isActive === false ? 'Unban user' : 'Ban user'}
                                    </p>
                                    <p className="text-[11px] text-neutral-500 mt-0.5">
                                        {customer.isActive === false
                                            ? 'Re-enable account access.'
                                            : 'Disable account. User data is preserved.'}
                                    </p>
                                </div>
                                {customer.isActive === false ? (
                                    <button
                                        onClick={() => setDangerAction('unban')}
                                        disabled={processingDanger}
                                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-success-600 bg-success-50 hover:bg-success-50/70 border border-success-500/30 rounded-md transition-colors disabled:opacity-50"
                                    >
                                        <Unlock size={12} /> Unban User
                                    </button>
                                ) : (
                                    <button
                                        onClick={() => setDangerAction('ban')}
                                        disabled={processingDanger}
                                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-error-600 bg-white hover:bg-error-50 border border-error-500/40 rounded-md transition-colors disabled:opacity-50"
                                    >
                                        <Ban size={12} /> Ban User
                                    </button>
                                )}
                            </div>
                            <div className="border-t border-error-500/20 pt-3 flex items-start justify-between gap-3">
                                <div className="flex-1">
                                    <p className="text-xs font-semibold text-neutral-800">Remove user</p>
                                    <p className="text-[11px] text-neutral-500 mt-0.5">
                                        Permanently delete the account and all data.
                                    </p>
                                </div>
                                <button
                                    onClick={() => setDangerAction('remove')}
                                    disabled={processingDanger}
                                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-error-600 hover:bg-error-500 rounded-md shadow-sm shadow-error-500/20 transition-colors disabled:opacity-50"
                                >
                                    <Trash2 size={12} /> Remove User
                                </button>
                            </div>
                        </div>
                    </div>

                </div>
            </div>

            {dangerAction && (
                <DangerousConfirmationModal
                    isOpen={!!dangerAction}
                    title={dangerCopy[dangerAction].title}
                    message={dangerCopy[dangerAction].message}
                    actionLabel={dangerCopy[dangerAction].label}
                    onConfirm={handleDangerConfirm}
                    onCancel={() => !processingDanger && setDangerAction(null)}
                    isProcessing={processingDanger}
                />
            )}

            <ConfirmationModal
                isOpen={modalConfig.isOpen}
                title={modalConfig.title}
                message={modalConfig.message}
                variant={modalConfig.variant}
                confirmText={modalConfig.confirmText}
                onConfirm={modalConfig.onConfirm}
                onCancel={closeModal}
            />
        </div >
    );
};
